/* Bare Knuckle Fighter — Theme-JS
   Portiert aus der React-App: Header-Zustand, Reveal, Parallax,
   Story-Zähler, Produktbild-Swipe, Varianten-Wahl, AJAX-Warenkorb + Drawer. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var drawerOpen = false;

  /* ---------- helpers ---------- */

  function themeString(key, fallback) {
    return (window.BKF && window.BKF.strings && window.BKF.strings[key]) || fallback;
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-AT', {
        style: 'currency',
        currency: (window.BKF && window.BKF.currency) || 'EUR'
      }).format(cents / 100);
    } catch (e) {
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }
  }

  var toastTimer = null;
  function showToast(text) {
    var toast = document.getElementById('bkf-toast');
    if (!toast) return;
    toast.textContent = '✓ ' + text + ' ' + themeString('addedToCart', '— im Warenkorb');
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.hidden = true;
    }, 2400);
  }

  /* ---------- header ---------- */

  function initHeader() {
    var header = document.getElementById('bkf-header');
    if (!header) return;
    var hideOverHero = header.dataset.hideOverHero === 'true';
    var heroHideReady = !hideOverHero || reducedMotion;

    function onScroll() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      header.classList.toggle('header--solid', scrollTop > 24);
      header.classList.toggle('header--hidden', heroHideReady && hideOverHero && scrollTop <= 1);
    }

    /* Beim ersten Rendern darf der Header nicht kurz in den versteckten
       Hero-Zustand wechseln, bevor der Browser seine Scrollposition restauriert.
       Sonst bleibt die Filter-/Transform-Ebene in WebKit teils unscharf stehen. */
    if (!heroHideReady) {
      header.classList.add('header--restoring');
      window.setTimeout(function () {
        heroHideReady = true;
        onScroll();
        window.requestAnimationFrame(function () {
          header.classList.remove('header--restoring');
        });
      }, 1500);
    }

    /* Browser stellen die Scrollposition bei Reload/Back teils deutlich nach
       DOMContentLoaded wieder her. Während dieses Fensters laufend abgleichen,
       statt von einem einzelnen kurzen Timeout abhängig zu sein. */
    var restoreFrame = 0;
    var restoreUntil = 0;
    function syncAfterScrollRestore() {
      onScroll();
      restoreUntil = Math.max(restoreUntil, performance.now() + 3000);
      if (restoreFrame) return;

      function syncFrame(now) {
        onScroll();
        if (now < restoreUntil) {
          restoreFrame = window.requestAnimationFrame(syncFrame);
        } else {
          restoreFrame = 0;
        }
      }

      restoreFrame = window.requestAnimationFrame(syncFrame);
    }

    /* Zusätzliche Absicherung für Browser, die beim Wiederherstellen der
       Scrollposition kein zuverlässiges scroll-Event auslösen. */
    if ('IntersectionObserver' in window) {
      var topSentinel = document.querySelector('[data-header-top-sentinel]');
      if (!topSentinel) {
        topSentinel = document.createElement('span');
        topSentinel.setAttribute('data-header-top-sentinel', '');
        topSentinel.setAttribute('aria-hidden', 'true');
        topSentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
        document.body.insertBefore(topSentinel, document.body.firstChild);
      }

      var topObserver = new IntersectionObserver(onScroll, { threshold: [0, 1] });
      topObserver.observe(topSentinel);
    }

    syncAfterScrollRestore();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('pageshow', syncAfterScrollRestore);
    window.addEventListener('load', syncAfterScrollRestore, { once: true });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) syncAfterScrollRestore();
    });

    var toggle = document.getElementById('bkf-menu-toggle');
    var links = document.getElementById('bkf-nav-links');
    if (toggle && links) {
      toggle.addEventListener('click', function () {
        var open = links.classList.toggle('nav__links--open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      links.addEventListener('click', function (e) {
        if (e.target.tagName === 'A') {
          links.classList.remove('nav__links--open');
          toggle.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  /* ---------- language selector ---------- */

  function initLanguageSelector() {
    var root = document.querySelector('[data-lang-select]');
    if (!root) return;
    var toggle = root.querySelector('[data-lang-toggle]');
    var list = root.querySelector('[data-lang-list]');
    if (!toggle || !list) return;

    function close() {
      list.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }
    function open() {
      list.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }

    toggle.addEventListener('click', function () {
      if (list.hidden) { open(); } else { close(); }
    });
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !list.hidden) {
        close();
        toggle.focus();
      }
    });
  }

  /* ---------- reveal on scroll ---------- */

  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -10% 0px'
    });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- hero parallax ---------- */

  function initParallax() {
    var bg = document.querySelector('[data-hero-bg]');
    var content = document.querySelector('[data-hero-content]');
    if ((!bg && !content) || reducedMotion) return;
    var raf = 0;
    function update() {
      raf = 0;
      var y = window.scrollY;
      if (y > window.innerHeight) return;
      if (bg) bg.style.transform = 'translateY(' + y * 0.25 + 'px)';
      if (content) content.style.transform = 'translateY(' + y * 0.12 + 'px)';
    }
    function onScroll() {
      if (!raf) raf = requestAnimationFrame(update);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- story counter ---------- */

  function initStoryCounter() {
    var title = document.querySelector('[data-story-title]');
    if (!title) return;
    var ups = title.querySelectorAll('[data-count-up]');
    var downs = title.querySelectorAll('[data-count-down]');

    function apply(progress) {
      var up = Math.round(progress * 100);
      ups.forEach(function (el) { el.textContent = up; });
      downs.forEach(function (el) { el.textContent = 100 - up; });
    }

    if (reducedMotion || !('IntersectionObserver' in window)) {
      apply(1);
      return;
    }
    apply(0);
    var io = new IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      io.disconnect();
      var t0 = performance.now();
      var duration = 1800;
      function tick(t) {
        var p = Math.min(1, (t - t0) / duration);
        apply(1 - Math.pow(1 - p, 3));
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(title);
  }

  /* ---------- collection filters ---------- */

  function initFilters() {
    var filters = document.querySelectorAll('.filter[data-filter]');
    var grid = document.querySelector('[data-product-grid]');
    if (!filters.length || !grid) return;

    var title = document.querySelector('[data-collection-title]');
    var params = new URLSearchParams(window.location.search);

    function applyFilter(btn, updateUrl) {
      filters.forEach(function (b) {
        b.classList.toggle('filter--active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });

      var type = btn.dataset.filter;
      grid.querySelectorAll('.product-card').forEach(function (card) {
        card.style.display = !type || card.dataset.productType === type ? '' : 'none';
      });

      if (title) {
        title.textContent = btn.dataset.filterTitle || title.dataset.defaultTitle;
      }

      if (updateUrl) {
        var url = new URL(window.location.href);
        if (type) {
          url.searchParams.set('filter', type);
        } else {
          url.searchParams.delete('filter');
        }
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    }

    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyFilter(btn, true);
      });
    });

    var initialFilter = params.get('filter');
    if (initialFilter) {
      var initialButton = Array.prototype.find.call(filters, function (btn) {
        return btn.dataset.filter === initialFilter;
      });
      if (initialButton) applyFilter(initialButton, false);
    }
  }

  /* ---------- legal documents ---------- */

  function legalKeyFromPath() {
    var path = window.location.pathname;
    if (path.indexOf('/policies/legal-notice') !== -1) return 'impressum';
    if (path.indexOf('/policies/contact-information') !== -1) return 'impressum';
    if (path.indexOf('/policies/terms-of-service') !== -1) return 'agb';
    if (path.indexOf('/policies/privacy-policy') !== -1) return 'datenschutz';
    if (path.indexOf('/policies/shipping-policy') !== -1) return 'versand';
    if (path.indexOf('/policies/refund-policy') !== -1) return 'widerruf';
    if (path.indexOf('/pages/impressum') !== -1) return 'impressum';
    if (path.indexOf('/pages/agb') !== -1) return 'agb';
    if (path.indexOf('/pages/datenschutz') !== -1) return 'datenschutz';
    if (path.indexOf('/pages/versand') !== -1) return 'versand';
    if (path.indexOf('/pages/widerruf') !== -1) return 'widerruf';
    return '';
  }

  function legalElement(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (typeof text === 'string') el.textContent = text;
    return el;
  }

  function trimLegalContent(holder) {
    function emptyText(node) {
      return node.nodeType === Node.TEXT_NODE && !node.textContent.trim();
    }
    while (holder.firstChild &&
      (emptyText(holder.firstChild) || holder.firstChild.nodeName === 'BR')) {
      holder.firstChild.remove();
    }
    while (holder.lastChild &&
      (emptyText(holder.lastChild) || holder.lastChild.nodeName === 'BR')) {
      holder.lastChild.remove();
    }
    return holder;
  }

  function legalRangeContent(source, startAfter, endBefore) {
    var range = document.createRange();
    range.selectNodeContents(source);
    if (startAfter) range.setStartAfter(startAfter);
    if (endBefore) range.setEndBefore(endBefore);
    var holder = legalElement('div', 'legal-richtext');
    holder.appendChild(range.cloneContents());
    return trimLegalContent(holder);
  }

  function hasLegalContent(holder) {
    return holder && (holder.textContent.trim() || holder.querySelector('img, video, iframe'));
  }

  function parseLegalSections(source) {
    var headings = Array.prototype.slice.call(source.querySelectorAll('h2, h3, strong'))
      .filter(function (heading) {
        if (heading.tagName === 'STRONG' && heading.closest('h2, h3')) return false;
        var text = heading.textContent.trim();
        return text && text.length < 140;
      });
    var intro = headings.length ? legalRangeContent(source, null, headings[0]) : null;
    var sections = headings.map(function (heading, index) {
      return {
        heading: heading.textContent.trim(),
        body: legalRangeContent(source, heading, headings[index + 1] || null)
      };
    }).filter(function (section) {
      return hasLegalContent(section.body);
    });

    if (!sections.length) {
      sections.push({
        heading: themeString('legalInformation', 'Informationen'),
        body: legalRangeContent(source, null, null)
      });
      intro = null;
    }
    return { intro: intro, sections: sections };
  }

  function legalStoreUrl(path) {
    var root = window.BKF && window.BKF.routes && window.BKF.routes.root;
    root = root || '/';
    return root.replace(/\/$/, '') + path;
  }

  function buildLegalHeader(title, key, chapterCount, compact) {
    var header = legalElement('header', 'legal-hero');
    var back = legalElement('a', 'page-head__back', themeString('legalBackHome', '← Zurück zum Start'));
    back.href = legalStoreUrl('/');
    header.appendChild(back);
    header.appendChild(legalElement('span', 'eyebrow', themeString('legalHeading', 'Rechtliches')));

    var row = legalElement('div', 'legal-hero__row');
    row.appendChild(legalElement('h1', 'page-head__title page-head__title--legal', title));
    var meta = compact
      ? (key === 'impressum'
        ? themeString('legalProviderInformation', 'Anbieterkennzeichnung')
        : themeString('legalDeliveryCosts', 'Lieferung & Kosten'))
      : chapterCount + ' ' + themeString(chapterCount === 1 ? 'legalChapter' : 'legalChapters', chapterCount === 1 ? 'Kapitel' : 'Kapitel');
    row.appendChild(legalElement('span', 'legal-hero__meta', meta));
    header.appendChild(row);

    var docs = [
      { key: 'impressum', label: themeString('legalNotice', 'Impressum'), path: '/policies/legal-notice' },
      { key: 'agb', label: themeString('legalTerms', 'AGB'), path: '/policies/terms-of-service' },
      { key: 'datenschutz', label: themeString('legalPrivacy', 'Datenschutz'), path: '/policies/privacy-policy' },
      { key: 'versand', label: themeString('legalShipping', 'Versand'), path: '/policies/shipping-policy' },
      { key: 'widerruf', label: themeString('legalRefund', 'Widerruf'), path: '/policies/refund-policy' }
    ];
    var nav = legalElement('nav', 'legal-docs');
    nav.setAttribute('aria-label', themeString('legalDocuments', 'Rechtliche Dokumente'));
    docs.forEach(function (doc) {
      var link = legalElement('a', 'legal-docs__link', doc.label);
      link.href = legalStoreUrl(doc.path);
      if (doc.key === key) {
        link.classList.add('legal-docs__link--active');
        link.setAttribute('aria-current', 'page');
      }
      nav.appendChild(link);
    });
    header.appendChild(nav);
    return header;
  }

  function buildCompactLegal(main, parsed, key) {
    var compact = legalElement('div', 'legal legal-compact legal-compact--' + key);
    if (hasLegalContent(parsed.intro)) {
      var intro = legalElement('section', 'legal-compact__intro');
      intro.appendChild(legalElement('span', 'legal-compact__eyebrow', 'DATRI SHOP'));
      intro.appendChild(parsed.intro);
      compact.appendChild(intro);
    }

    var grid = legalElement('div', 'legal-compact__grid');
    parsed.sections.forEach(function (section, index) {
      var card = legalElement('section', 'legal-compact__card');
      var head = legalElement('div', 'legal-compact__card-head');
      head.appendChild(legalElement('span', '', String(index + 1).padStart(2, '0')));
      head.appendChild(legalElement('h2', '', section.heading));
      card.appendChild(head);
      var body = legalElement('div', 'legal-compact__body');
      body.appendChild(section.body);
      card.appendChild(body);
      grid.appendChild(card);
    });
    compact.appendChild(grid);
    main.appendChild(compact);
  }

  function buildLongLegal(main, parsed) {
    var layout = legalElement('div', 'legal-layout');
    var aside = legalElement('aside', 'legal-nav');
    aside.setAttribute('aria-label', themeString('legalContents', 'Inhaltsverzeichnis'));
    var top = legalElement('div', 'legal-nav__topline');
    top.appendChild(legalElement('span', '', themeString('legalContents', 'Inhalt')));
    top.appendChild(legalElement('span', '', String(parsed.sections.length).padStart(2, '0')));
    aside.appendChild(top);
    var list = legalElement('ol', 'legal-nav__list');

    var content = legalElement('div', 'legal is-revealed');
    var toolbar = legalElement('div', 'legal__toolbar');
    toolbar.appendChild(legalElement('span', '', themeString('legalReadDocument', 'Dokument lesen')));
    var toggleAll = legalElement('button', '', themeString('legalOpenAll', 'Alle öffnen'));
    toggleAll.type = 'button';
    toolbar.appendChild(toggleAll);
    content.appendChild(toolbar);

    if (hasLegalContent(parsed.intro)) {
      var intro = legalElement('section', 'legal__intro');
      intro.appendChild(parsed.intro);
      content.appendChild(intro);
    }

    var details = [];
    parsed.sections.forEach(function (section, index) {
      var id = 'legal-section-' + (index + 1);
      var item = legalElement('li');
      var link = legalElement('a');
      link.href = '#' + id;
      link.appendChild(legalElement('span', '', String(index + 1).padStart(2, '0')));
      link.appendChild(document.createTextNode(section.heading));
      item.appendChild(link);
      list.appendChild(item);

      var detail = legalElement('details', 'legal__section legal__section--collapsible');
      detail.id = id;
      detail.open = index === 0;
      var summary = legalElement('summary', 'legal__summary');
      summary.appendChild(legalElement('span', 'legal__number', String(index + 1).padStart(2, '0')));
      summary.appendChild(legalElement('h2', 'legal__heading', section.heading));
      var icon = legalElement('span', 'legal__toggle');
      icon.setAttribute('aria-hidden', 'true');
      summary.appendChild(icon);
      detail.appendChild(summary);
      var body = legalElement('div', 'legal__body');
      body.appendChild(section.body);
      detail.appendChild(body);
      content.appendChild(detail);
      details.push(detail);

      link.addEventListener('click', function (event) {
        event.preventDefault();
        detail.open = true;
        detail.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '#' + id);
        }
      });
    });
    aside.appendChild(list);
    layout.appendChild(aside);
    layout.appendChild(content);
    main.appendChild(layout);

    toggleAll.addEventListener('click', function () {
      var shouldOpen = !details.every(function (detail) { return detail.open; });
      details.forEach(function (detail) { detail.open = shouldOpen; });
      toggleAll.textContent = shouldOpen
        ? themeString('legalCloseAll', 'Alle schließen')
        : themeString('legalOpenAll', 'Alle öffnen');
    });
  }

  function initLegalDocuments() {
    var sourceContainer = document.querySelector('.shopify-policy__container');
    if (!sourceContainer || document.querySelector('[data-legal-ready]')) return;
    var source = sourceContainer.querySelector('.shopify-policy__body .rte');
    var titleEl = sourceContainer.querySelector('.shopify-policy__title h1');
    if (!source || !titleEl) return;

    var key = sourceContainer.dataset.legalKey || legalKeyFromPath();
    if (!key) return;
    var parsed = parseLegalSections(source);
    var compact = key === 'impressum' || key === 'versand';
    var main = legalElement('main', 'page legal-page');
    main.setAttribute('data-legal-ready', 'true');
    var legalTitles = {
      impressum: themeString('legalNotice', 'Impressum'),
      agb: themeString('legalTerms', 'AGB'),
      datenschutz: themeString('legalPrivacy', 'Datenschutz'),
      versand: themeString('legalShipping', 'Versand'),
      widerruf: themeString('legalRefund', 'Widerruf')
    };
    var title = legalTitles[key] || titleEl.textContent.trim();
    main.appendChild(buildLegalHeader(title, key, parsed.sections.length, compact));
    if (compact) buildCompactLegal(main, parsed, key);
    else buildLongLegal(main, parsed);
    sourceContainer.replaceWith(main);

    if (window.location.hash) {
      var target = main.querySelector(window.location.hash);
      if (target && target.tagName === 'DETAILS') target.open = true;
    }
  }

  /* ---------- mobile featured carousel ---------- */

  function initFeaturedCarousels() {
    document.querySelectorAll('[data-featured-carousel]').forEach(function (root) {
      if (root.dataset.featuredReady === 'true') return;

      var track = root.querySelector('[data-featured-track]');
      var slides = track ? Array.from(track.querySelectorAll('[data-featured-slide]')) : [];
      var controls = root.querySelector('[data-featured-controls]');
      if (!track || !controls || slides.length < 2) return;

      root.dataset.featuredReady = 'true';
      controls.hidden = false;

      var currentEl = root.querySelector('[data-featured-current]');
      var totalEl = root.querySelector('[data-featured-total]');
      var prev = root.querySelector('[data-featured-prev]');
      var next = root.querySelector('[data-featured-next]');
      var media = window.matchMedia('(max-width: 760px)');
      var autoplay = root.dataset.featuredAutoplay !== 'false';
      var duration = parseInt(root.dataset.featuredDuration, 10) || 5000;
      var index = 0;
      var timer;
      var scrollTimer;
      var inView = true;

      root.style.setProperty('--featured-duration', duration + 'ms');
      totalEl.textContent = String(slides.length).padStart(2, '0');

      function updateCount() {
        currentEl.textContent = String(index + 1).padStart(2, '0');
      }

      function stop() {
        window.clearTimeout(timer);
        root.classList.remove('featured-carousel--playing');
      }

      function restart() {
        stop();
        if (!media.matches || !autoplay || reducedMotion || !inView || document.hidden) return;
        void root.offsetWidth;
        root.classList.add('featured-carousel--playing');
        timer = window.setTimeout(function () {
          goTo(index + 1);
        }, duration);
      }

      function goTo(nextIndex) {
        index = (nextIndex + slides.length) % slides.length;
        updateCount();
        track.scrollTo({
          left: index * track.clientWidth,
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
        restart();
      }

      function syncFromScroll() {
        if (!track.clientWidth) return;
        var nextIndex = Math.round(track.scrollLeft / track.clientWidth);
        nextIndex = Math.max(0, Math.min(slides.length - 1, nextIndex));
        if (nextIndex !== index) {
          index = nextIndex;
          updateCount();
        }
        restart();
      }

      prev.addEventListener('click', function () { goTo(index - 1); });
      next.addEventListener('click', function () { goTo(index + 1); });
      track.addEventListener('pointerdown', stop);
      track.addEventListener('pointerup', function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(syncFromScroll, 80);
      });
      track.addEventListener('scroll', function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(syncFromScroll, 120);
      }, { passive: true });
      document.addEventListener('visibilitychange', restart);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting;
          restart();
        }, { threshold: 0.25 }).observe(root);
      }

      if (media.addEventListener) media.addEventListener('change', restart);
      else media.addListener(restart);

      updateCount();
      restart();
    });
  }

  /* ---------- product page ---------- */

  function initProduct() {
    var root = document.querySelector('[data-product]');
    if (!root) return;

    if (root.dataset.productReady === 'true') return;
    root.dataset.productReady = 'true';

    /* Desktop: Thumbnail-Wechsel. Mobile: echtes Swipe-Karussell. */
    var productGallery = root.querySelector('[data-pdp-gallery]');
    var productSlides = productGallery ? Array.from(productGallery.querySelectorAll('[data-pdp-slide]')) : [];
    var productThumbs = Array.from(root.querySelectorAll('[data-thumb]'));
    var productMedia = window.matchMedia('(max-width: 760px)');
    var productIndex = 0;
    var productScrollTimer;

    function setProductImage(nextIndex, scrollGallery) {
      productIndex = Math.max(0, Math.min(productSlides.length - 1, nextIndex));
      productSlides.forEach(function (slide, index) {
        slide.classList.toggle('pdp__slide--active', index === productIndex);
      });
      productThumbs.forEach(function (thumb, index) {
        var active = index === productIndex;
        thumb.classList.toggle('pdp__thumb--active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (scrollGallery && productGallery && productMedia.matches) {
        productGallery.scrollTo({
          left: productIndex * productGallery.clientWidth,
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      }
    }

    productThumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        setProductImage(parseInt(thumb.dataset.imageIndex, 10) || 0, true);
      });
    });

    if (productGallery && productSlides.length > 1) {
      productGallery.addEventListener('scroll', function () {
        if (!productMedia.matches || !productGallery.clientWidth) return;
        window.clearTimeout(productScrollTimer);
        productScrollTimer = window.setTimeout(function () {
          setProductImage(Math.round(productGallery.scrollLeft / productGallery.clientWidth), false);
        }, 80);
      }, { passive: true });
    }

    setProductImage(0, false);

    /* Varianten */
    var variantsScript = root.querySelector('[data-variants-json]');
    var variants = variantsScript ? JSON.parse(variantsScript.textContent) : null;
    var optionGroups = root.querySelectorAll('[data-option-index]');
    var selected = [];
    var priceEl = root.querySelector('[data-price]');
    var addBtn = root.querySelector('[data-add-to-cart]');

    function currentVariant() {
      if (!variants) return null;
      return variants.find(function (v) {
        var opts = [v.option1, v.option2, v.option3];
        return selected.every(function (value, i) {
          return opts[i] === value;
        });
      });
    }

    function updateVariantUi(variant, updateUrl) {
      if (!variant) return;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? addBtn.dataset.addLabel : addBtn.dataset.soldOutLabel;
      }

      if (variant.featured_image && variant.featured_image.id) {
        var variantSlide = productSlides.findIndex(function (slide) {
          return String(slide.dataset.imageId) === String(variant.featured_image.id);
        });
        if (variantSlide >= 0) setProductImage(variantSlide, true);
      }

      if (updateUrl && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    }

    function selectVariantOptions(variant) {
      if (!variant) return;
      var values = [variant.option1, variant.option2, variant.option3];
      optionGroups.forEach(function (group) {
        var index = parseInt(group.dataset.optionIndex, 10);
        selected[index] = values[index];
        group.querySelectorAll('[data-option-value]').forEach(function (btn) {
          var active = btn.dataset.optionValue === values[index];
          btn.classList.toggle('size-option--active', active);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });
    }

    optionGroups.forEach(function (group) {
      var index = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('[data-option-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          selected[index] = btn.dataset.optionValue;
          group.querySelectorAll('[data-option-value]').forEach(function (b) {
            var active = b === btn;
            b.classList.toggle('size-option--active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          var hint = group.querySelector('[data-size-hint]');
          if (hint) hint.hidden = true;
          group.querySelector('.size-picker').classList.remove('size-picker--error');

          updateVariantUi(currentVariant(), true);
        });
      });
    });

    if (variants && variants.length) {
      var initialVariant = variants.find(function (variant) {
        return String(variant.id) === String(root.dataset.selectedVariantId);
      }) || variants.find(function (variant) { return variant.available; }) || variants[0];
      selectVariantOptions(initialVariant);
      updateVariantUi(initialVariant, false);
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var variantId = addBtn.dataset.variantId;
        if (!variantId && variants) {
          /* Alle Optionen gewählt? Sonst Hinweis an den offenen Gruppen */
          var missing = false;
          optionGroups.forEach(function (group) {
            var index = parseInt(group.dataset.optionIndex, 10);
            if (!selected[index]) {
              missing = true;
              var hint = group.querySelector('[data-size-hint]');
              if (hint) hint.hidden = false;
              group.querySelector('.size-picker').classList.add('size-picker--error');
            }
          });
          if (missing) return;
          var variant = currentVariant();
          if (!variant || !variant.available) return;
          variantId = variant.id;
        }
        if (!variantId) return;
        addToCart(variantId, addBtn.dataset.productTitle);
      });
    }
  }

  /* ---------- cart drawer ---------- */

  function drawerEls() {
    return {
      drawer: document.querySelector('[data-drawer]'),
      backdrop: document.querySelector('[data-drawer-backdrop]')
    };
  }

  function setDrawer(open) {
    drawerOpen = open;
    var els = drawerEls();
    if (!els.drawer) return;
    els.drawer.classList.toggle('drawer--open', open);
    els.drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (els.backdrop) els.backdrop.classList.toggle('drawer-backdrop--visible', open);
    document.body.style.overflow = open ? 'hidden' : '';
  }

  function replaceDrawer(sectionHtml) {
    var root = document.getElementById('bkf-cart-drawer-root');
    if (!root || !sectionHtml) return;
    var tpl = document.createElement('template');
    tpl.innerHTML = sectionHtml;
    var fresh = tpl.content.querySelector('#bkf-cart-drawer-root');
    if (!fresh) return;
    root.replaceWith(fresh);
    if (drawerOpen) {
      /* Ohne Transition wieder öffnen */
      var els = drawerEls();
      if (els.drawer) {
        els.drawer.style.transition = 'none';
        setDrawer(true);
        void els.drawer.offsetHeight;
        els.drawer.style.transition = '';
      }
    }
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count < 1;
    });
  }

  function refreshDrawerSection() {
    return fetch(window.location.pathname + '?sections=bkf-cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) { replaceDrawer(data['bkf-cart-drawer']); });
  }

  function refreshCartCount() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartCount(cart.item_count); });
  }

  function addToCart(variantId, title) {
    fetch(window.BKF.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: 'bkf-cart-drawer'
      })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function (data) {
        if (data.sections) replaceDrawer(data.sections['bkf-cart-drawer']);
        refreshCartCount();
        if (title) showToast(title);
      })
      .catch(function () {
        window.location.href = window.BKF.routes.cart;
      });
  }

  function changeQuantity(line, quantity) {
    fetch(window.BKF.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: parseInt(line, 10),
        quantity: parseInt(quantity, 10),
        sections: 'bkf-cart-drawer'
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.sections) replaceDrawer(cart.sections['bkf-cart-drawer']);
        updateCartCount(cart.item_count);
      });
  }

  /* Delegiert, damit der neu gerenderte Drawer weiter funktioniert */
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('#bkf-cart-open');
    if (openBtn) {
      setDrawer(true);
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-drawer-backdrop]')) {
      setDrawer(false);
      return;
    }
    var qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      changeQuantity(qtyBtn.dataset.line, qtyBtn.dataset.quantity);
      return;
    }
    var quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      addToCart(quickAdd.dataset.variantId, quickAdd.dataset.productTitle);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerOpen) setDrawer(false);
  });

  /* ---------- newsletter popup ---------- */

  function initNewsletterPopup() {
    var popup = document.querySelector('[data-newsletter-popup]');
    var newsletter = document.getElementById('newsletter');
    var story = document.getElementById('story');
    if (!popup || !newsletter || !story || popup.dataset.initialized === 'true') return;
    popup.dataset.initialized = 'true';

    var storageKey = 'datriNewsletterPopupSeenAt';
    var repeatAfter = 7 * 24 * 60 * 60 * 1000;
    var previousFocus = null;
    var timer = null;
    var isOpen = false;
    var cancelled = false;

    function readSeenAt() {
      try {
        return parseInt(window.localStorage.getItem(storageKey), 10) || 0;
      } catch (e) {
        return 0;
      }
    }

    function markSeen() {
      try {
        window.localStorage.setItem(storageKey, String(Date.now()));
      } catch (e) {
        /* Private browsing may block storage; the popup still works. */
      }
    }

    function closePopup(restoreFocus) {
      if (!isOpen) return;
      isOpen = false;
      popup.classList.remove('newsletter-popup--visible');
      document.body.classList.remove('newsletter-popup-open');
      window.setTimeout(function () {
        popup.hidden = true;
      }, reducedMotion ? 0 : 280);
      if (restoreFocus && previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    }

    function openPopup() {
      if (isOpen || cancelled) return;
      isOpen = true;
      previousFocus = document.activeElement;
      popup.hidden = false;
      document.body.classList.add('newsletter-popup-open');
      window.requestAnimationFrame(function () {
        popup.classList.add('newsletter-popup--visible');
        var closeButton = popup.querySelector('.newsletter-popup__close');
        if (closeButton) closeButton.focus({ preventScroll: true });
      });
    }

    function privacyBannerIsVisible() {
      var banner = document.getElementById('shopify-pc__banner') ||
        document.querySelector('.shopify-pc__banner__dialog');
      if (!banner || banner.hidden || banner.getAttribute('aria-hidden') === 'true') return false;
      var style = window.getComputedStyle(banner);
      return style.display !== 'none' && style.visibility !== 'hidden' && banner.getClientRects().length > 0;
    }

    function openWhenAvailable() {
      if (cancelled) return;
      if (privacyBannerIsVisible()) {
        timer = window.setTimeout(openWhenAvailable, 1800);
        return;
      }
      openPopup();
    }

    if (newsletter.querySelector('.newsletter__done')) {
      markSeen();
      return;
    }

    var seenAt = readSeenAt();
    if (seenAt && Date.now() - seenAt < repeatAfter) return;

    popup.querySelectorAll('[data-newsletter-popup-close]').forEach(function (button) {
      button.addEventListener('click', function () {
        markSeen();
        closePopup(true);
      });
    });

    var cta = popup.querySelector('[data-newsletter-popup-cta]');
    if (cta) {
      cta.addEventListener('click', function (event) {
        event.preventDefault();
        markSeen();
        closePopup(false);
        window.setTimeout(function () {
          newsletter.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
          var email = newsletter.querySelector('input[type="email"]');
          if (email) {
            window.setTimeout(function () { email.focus({ preventScroll: true }); }, reducedMotion ? 0 : 650);
          }
        }, reducedMotion ? 0 : 220);
      });
    }

    popup.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        markSeen();
        closePopup(true);
      }
    });

    if ('IntersectionObserver' in window) {
      var storyObserver = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        storyObserver.disconnect();
        openWhenAvailable();
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -20% 0px'
      });
      storyObserver.observe(story);
    } else {
      var onStoryScroll = function () {
        var rect = story.getBoundingClientRect();
        if (rect.top > window.innerHeight * 0.8 || rect.bottom <= 0) return;
        window.removeEventListener('scroll', onStoryScroll);
        openWhenAvailable();
      };
      window.addEventListener('scroll', onStoryScroll, { passive: true });
      onStoryScroll();
    }

    if ('IntersectionObserver' in window) {
      var newsletterObserver = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        newsletterObserver.disconnect();
        if (!isOpen) {
          cancelled = true;
          window.clearTimeout(timer);
        }
      }, { threshold: 0.2 });
      newsletterObserver.observe(newsletter);
    }
  }

  /* ---------- init ---------- */

  function init() {
    initHeader();
    initLanguageSelector();
    initLegalDocuments();
    initFeaturedCarousels();
    initReveal();
    initParallax();
    initStoryCounter();
    initFilters();
    initProduct();
    initNewsletterPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme-Editor: Sections werden live neu geladen */
  document.addEventListener('shopify:section:load', function () {
    initLegalDocuments();
    initFeaturedCarousels();
    initReveal();
    initParallax();
    initStoryCounter();
    initFilters();
    initProduct();
    initNewsletterPopup();
  });
})();
